import {
  LayoutDashboard,
  Users,
  Building2,
  MapPin,
  FileText,
  CreditCard,
  Mail,
  Palette,
  Trophy,
  Store,
  Gift,
  Settings,
  Package,
  BarChart3,
  Plus,
  Coffee,
  LayoutTemplate,
  Truck,
  LayoutGrid,
  ListOrdered,
  Tags,
  CalendarClock,
  Printer,
  Archive,
  BadgeDollarSign,
  TrendingUp,
  Inbox,
} from 'lucide-react';
import { flag } from '../../config';

/**
 * Navigation link configurations for each dashboard type.
 *
 * One source of truth: the build*NavLinks() functions below evaluate flag() when they
 * are called, so a flag that arrives at runtime (GET /api/flags) is seen by callers that
 * build on render (components/portal/nav.js getPortalNav, Phase 7a). The *_NAV_LINKS
 * constants are the same lists built once at import time, kept unchanged for the legacy
 * portal headers (DashboardMobileNav).
 */

export function buildAdminNavLinks() {
  // SLOT_MODEL (Phase 9): the rebuilt admin portal. Same URLs as the swapped routes in
  // routes/admin.jsx ("Products & prices" is /admin/subscriptions), plus the pages whose routes
  // exist only with this flag on (Inventory, Category map, Waitlist, Categories, Renewals,
  // Billing, Print) and the archived lists. Coaster Designs goes (its route redirects to Print).
  // Each path shared by both lists is written once; the flag picks labels and order.
  const slot = flag('SLOT_MODEL');
  // Phase 11: with the flag on /admin/dashboard is the rebuilt Overview (AdminOverview), and
  // "Owner KPIs" (/admin/owner) follows it. Owner KPIs shows to every admin: the page itself
  // tells anyone who is not the owner that it is owner-only (the API answers 403 not_owner).
  const dashboard = { icon: LayoutDashboard, label: 'Overview', path: '/admin/dashboard' };
  const ownerKpis = { icon: TrendingUp, label: 'Owner KPIs', path: '/admin/owner' };
  const customers = { icon: Users, label: slot ? 'Customers' : 'Users', path: '/admin/customers' };
  const businesses = { icon: Building2, label: 'Businesses', path: '/admin/businesses' };
  const venues = { icon: MapPin, label: slot ? 'Venues' : 'Locations', path: '/admin/locations' };
  const applications = {
    icon: FileText,
    label: slot ? 'Venue applications' : 'Venue Applications',
    path: '/admin/venue-applications',
    badgeKey: 'pendingApplications',
  };
  // Public-site redesign: the enquiries the site's forms store (route flag on only). The
  // Enquiries page passes the count of new ones as its badge.
  const enquiries = { icon: Inbox, label: 'Enquiries', path: '/admin/enquiries', badgeKey: 'newEnquiries' };
  const products = { icon: slot ? BadgeDollarSign : CreditCard, label: slot ? 'Products & prices' : 'Subscriptions', path: '/admin/subscriptions' };
  const emailTemplates = { icon: Mail, label: slot ? 'Email templates' : 'Email Templates', path: '/admin/email-templates' };
  const rewards = flag('REWARDS') ? [{ icon: Trophy, label: 'Rewards', path: '/admin/rewards' }] : [];
  // Coaster Studio (COASTER_STUDIO, in the rebuilt admin portal only): venue coaster designs, next to
  // Print. Its routes exist only with SLOT_MODEL and COASTER_STUDIO on (routes/admin.jsx).
  const coasterStudio = flag('COASTER_STUDIO')
    ? [{ icon: Palette, label: 'Coaster Studio', path: '/admin/coaster-studio' }] : [];
  const profile = { icon: Settings, label: slot ? 'My profile' : 'My Profile', path: '/admin/profile' };
  if (!slot) {
    return [
      { section: 'Dashboard', links: [dashboard] },
      { section: 'Management', links: [customers, businesses, venues, applications] },
      {
        section: 'Settings',
        links: [
          products,
          emailTemplates,
          { icon: Palette, label: 'Coaster Designs', path: '/admin/coaster-designs' },
          ...rewards,
          profile,
        ],
      },
    ];
  }
  return [
    { section: 'Dashboard', links: [dashboard, ownerKpis] },
    {
      section: 'Venues & inventory',
      links: [
        venues,
        { icon: Package, label: 'Inventory', path: '/admin/inventory' },
        { icon: LayoutGrid, label: 'Category map', path: '/admin/category-map' },
        { icon: ListOrdered, label: 'Waitlist', path: '/admin/waitlist' },
        { icon: Tags, label: 'Categories', path: '/admin/categories' },
      ],
    },
    { section: 'Accounts', links: [businesses, customers, applications, enquiries] },
    {
      section: 'Billing & print',
      links: [
        { icon: CalendarClock, label: 'Renewals', path: '/admin/renewals' },
        { icon: CreditCard, label: 'Billing', path: '/admin/billing' },
        { icon: Printer, label: 'Print', path: '/admin/print' },
        ...coasterStudio,
        products,
      ],
    },
    {
      section: 'Archived lists',
      links: [
        { icon: Archive, label: 'Archived venues', path: '/admin/locations/archived' },
        { icon: Archive, label: 'Archived businesses', path: '/admin/businesses/archived' },
        { icon: Archive, label: 'Archived customers', path: '/admin/customers/archived' },
      ],
    },
    { section: 'Settings', links: [emailTemplates, ...rewards, profile] },
  ];
}

export function buildBusinessNavLinks() {
  // SLOT_MODEL (Phase 7c): the rebuilt slot portal. Same URLs as the swapped routes in
  // routes/business.jsx (deals are "Offers"), plus Leads, Reports (7c), Your page (7d) and Coasters (8B),
  // whose routes exist only with this flag on. Each path is written once; the flag picks labels and order.
  const slot = flag('SLOT_MODEL');
  const dashboard = { icon: LayoutDashboard, label: 'Dashboard', path: '/business/dashboard' };
  const details = { icon: Store, label: slot ? 'Business details' : 'Business Details', path: '/business/settings' };
  const deals = { icon: Gift, label: slot ? 'Offers' : 'Deals', path: '/business/deals', badgeKey: 'activeDeals' };
  // SLOT_MODEL + SLOT_BILLING (Phase 7b): "Slots & billing" replaces the legacy Local/Multi
  // "Subscription" page in the nav (the route itself stays, flags not deletion). Same gate as
  // the /business/billing route (routes/business.jsx). SLOT_MODEL alone (Phase 12): no
  // billing entry at all, since the legacy subscription pages redirect to the dashboard.
  const billing = slot && flag('SLOT_BILLING')
    ? { icon: CreditCard, label: 'Slots & billing', path: '/business/billing' }
    : { icon: CreditCard, label: 'Subscription', path: '/business/subscription-manage' };
  const slotBillingLinks = slot && !flag('SLOT_BILLING') ? [] : [billing];
  return [
    {
      section: 'Navigation',
      links: slot
        ? [
          dashboard,
          deals,
          { icon: Users, label: 'Leads', path: '/business/leads', badgeKey: 'newLeads' },
          { icon: BarChart3, label: 'Reports', path: '/business/reports' },
          // Phase 7d: the Premium landing page / Sponsor microsite editor. Shown to every
          // advertiser (the page explains that Standard and free listings have none); its
          // routes exist only with SLOT_MODEL on (routes/business.jsx).
          { icon: LayoutTemplate, label: 'Your page', path: '/business/pages' },
          // Phase 8B: coaster artwork, proofs and deliveries per venue.
          { icon: Coffee, label: 'Coasters', path: '/business/coasters' },
          details,
          ...slotBillingLinks,
        ]
        : [dashboard, details, deals, billing],
    },
    {
      section: 'Quick Actions',
      links: [
        { icon: Plus, label: slot ? 'Create offer' : 'Create New Deal', path: '/business/deals/new', isAction: true },
      ],
    },
  ];
}

export function buildVenueNavLinks() {
  // SLOT_MODEL (Phase 8B): the rebuilt venue portal. Coasters and Analytics give way to
  // Deliveries (their routes redirect, routes/venue.jsx) and the reorder quick action goes
  // (coasters now arrive with each quarterly print cycle). Each path is written once.
  const slot = flag('SLOT_MODEL');
  const dashboard = { icon: LayoutDashboard, label: 'Dashboard', path: '/venue/dashboard' };
  const advertisers = { icon: Building2, label: 'Advertisers', path: '/venue/advertisers', badgeKey: 'advertiserCount' };
  const settings = { icon: Settings, label: 'Settings', path: '/venue/settings' };
  if (slot) {
    return [
      {
        section: 'Navigation',
        links: [
          dashboard,
          { icon: Truck, label: 'Deliveries', path: '/venue/deliveries' },
          advertisers,
          settings,
        ],
      },
    ];
  }
  return [
    {
      section: 'Navigation',
      links: [
        dashboard,
        // PD-13: the inventory number is hidden, so no badge here.
        { icon: Coffee, label: 'Coasters', path: '/venue/coasters' },
        ...(flag('VENUE_ANALYTICS') ? [{ icon: BarChart3, label: 'Analytics', path: '/venue/analytics' }] : []),
        advertisers,
        settings,
      ],
    },
    ...(flag('COASTER_INVENTORY') ? [{
      section: 'Quick Actions',
      links: [
        { icon: Package, label: 'Request More Coasters', path: '/venue/coasters', isAction: true },
      ],
    }] : []),
  ];
}

// Import-time snapshots (flag state when this module first loads), as before.
export const ADMIN_NAV_LINKS = buildAdminNavLinks();
export const BUSINESS_NAV_LINKS = buildBusinessNavLinks();
export const VENUE_NAV_LINKS = buildVenueNavLinks();

export function getNavLinksForVariant(variant) {
  switch (variant) {
    case 'admin':
      return ADMIN_NAV_LINKS;
    case 'business':
      return BUSINESS_NAV_LINKS;
    case 'venue':
      return VENUE_NAV_LINKS;
    default:
      return [];
  }
}
